import React from 'react';
import { Box, Paper, Stack, Tooltip, Typography, alpha } from '@mui/material';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';
import { fmtMetricValue, fmtPercent } from './formatters';
import { STATUS_COLORS, STATUS_LABELS } from './StatusChip';
import type { ChainMetric, ChainStage } from './types';

function DeltaIcon({ direction }: { direction: ChainMetric['direction'] }) {
  if (direction === 'up') return <ArrowUpwardRounded sx={{ fontSize: 12 }} />;
  if (direction === 'down') return <ArrowDownwardRounded sx={{ fontSize: 12 }} />;
  return <RemoveRounded sx={{ fontSize: 12 }} />;
}

function sentimentColor(metric: ChainMetric): string {
  if (metric.sentiment === 'bad') return 'error.main';
  if (metric.sentiment === 'good') return 'success.main';
  return 'text.secondary';
}

interface StageCardProps {
  stage: ChainStage;
  selected: boolean;
  onClick: () => void;
  currencySymbol: string;
}

function StageCard({ stage, selected, onClick, currencySymbol }: StageCardProps) {
  const color = STATUS_COLORS[stage.status];
  const { metric } = stage;

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={(t) => ({
        p: 1.25,
        cursor: 'pointer',
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        borderColor: selected ? t.palette.primary.main : alpha(color, 0.3),
        bgcolor: selected ? alpha(t.palette.primary.main, 0.06) : undefined,
        borderTop: `3px solid ${color}`,
        transition: `border-color ${t.ds?.motion?.duration?.fast ?? 150}ms, background-color ${t.ds?.motion?.duration?.fast ?? 150}ms`,
        '&:hover': { bgcolor: selected ? alpha(t.palette.primary.main, 0.09) : t.palette.action.hover },
        '&:focus-visible': { outline: 'none', boxShadow: t.tokens.shadow.focusRing },
      })}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
        <Typography variant="caption" fontWeight={800} noWrap sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary' }}>
          {stage.order}. {stage.name}
        </Typography>
        <Tooltip title={STATUS_LABELS[stage.status]} arrow>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        </Tooltip>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
        <Typography variant="h6" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmtMetricValue(metric, currencySymbol)}
        </Typography>
        {metric.pct_change != null && (
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: sentimentColor(metric) }}>
            <DeltaIcon direction={metric.direction} />
            <Typography variant="caption" fontWeight={700}>
              {fmtPercent(metric.pct_change)}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" noWrap>
        {metric.label}
      </Typography>
    </Paper>
  );
}

export interface ChainViewProps {
  stages: ChainStage[];
  selectedStageId: string | null;
  onSelectStage: (stageId: string) => void;
  currencySymbol?: string;
}

/** Generic flat chain-of-stages view — N stages, each independently color-coded.
 * Reusable across any future chain-view monitor; carries no domain-specific
 * grouping (unlike Lead-to-Cash's 5-phase grouping, which is the customer-specific). */
export default function ChainView({ stages, selectedStageId, onSelectStage, currencySymbol = '$' }: ChainViewProps) {
  const sorted = [...stages].sort((a, b) => a.order - b.order);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: `repeat(${sorted.length}, minmax(160px, 1fr))`,
          md: `repeat(${sorted.length}, minmax(0, 1fr))`,
        },
        gap: 1,
        overflowX: { xs: 'auto', md: 'visible' },
      }}
    >
      {sorted.map((stage) => (
        <StageCard
          key={stage.id}
          stage={stage}
          selected={selectedStageId === stage.id}
          onClick={() => onSelectStage(stage.id)}
          currencySymbol={currencySymbol}
        />
      ))}
    </Box>
  );
}
